// src/pages/ingresos/components/OperationsModal.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "../../../utils/supabase";

const PRIORITIES = [
  { value: "low", label: "Baja" },
  { value: "medium", label: "Media" },
  { value: "high", label: "Alta" },
  { value: "urgent", label: "Urgente" },
];

const SERVICE_ORDER_STATUSES = [
  { value: "delivered_to_lab", label: "Entregado a laboratorio (ADM-Paola)" },
  { value: "calibration_finished", label: "Calibración finalizada (LAB-Técn. asignado)" },
  {
    value: "certificates_finished_controlled",
    label: "Certificados finalizados y controlados (LAB-Diego)",
  },
  { value: "uploaded_for_remito", label: "Subido para remitar (LAB-Diego)" },
  { value: "remito_generated", label: "Remito generado (ADM-Paola)" },
  { value: "client_notified", label: "Cliente avisado (ADM-Paola/Dali)" },
];

const LEGACY_STATUS_TO_NEW = {
  pending: "delivered_to_lab",
  in_progress: "calibration_finished",
  completed: "client_notified",
  blocked: "uploaded_for_remito",
};

const normalizeServiceOrderStatus = (status) => {
  const s = String(status || "").trim();
  if (!s) return "delivered_to_lab";
  if (SERVICE_ORDER_STATUSES.some((x) => x.value === s)) return s;
  return LEGACY_STATUS_TO_NEW[s] || "delivered_to_lab";
};

export default function OperationsModal({ po, open, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    ops_priority: "medium",
    ops_status: "delivered_to_lab",
    ops_due_date: "",
    ops_notes: "",
  });

  // Cargar valores actuales cuando se abre el modal.
  // Estado: priorizamos service_orders.status (estado real de OS).
  useEffect(() => {
    if (!open || !po) return;

    let alive = true;

    const loadCurrentStatus = async () => {
      setForm({
        ops_priority: po.ops_priority || "medium",
        ops_status: normalizeServiceOrderStatus(po.ops_status),
        ops_due_date: po.ops_due_date || "",
        ops_notes: po.ops_notes || "",
      });

      try {
        const { data, error } = await supabase
          .from("service_orders")
          .select("status, created_at")
          .eq("po_id", po.id)
          .order("created_at", { ascending: false })
          .limit(1);

        if (error) throw error;
        if (!alive) return;

        const soStatus = data?.[0]?.status;
        if (soStatus) {
          setForm((prev) => ({
            ...prev,
            ops_status: normalizeServiceOrderStatus(soStatus),
          }));
        }
      } catch {
        // Mantener fallback de la OC si no se puede leer OS.
      }
    };

    loadCurrentStatus();
    return () => {
      alive = false;
    };
  }, [open, po]);

  if (!open || !po) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Mantener metadata operativa en PO
      const { error } = await supabase
        .from("purchase_orders")
        .update({
          ops_priority: form.ops_priority,
          ops_status: form.ops_status,
          ops_due_date: form.ops_due_date || null,
          ops_notes: form.ops_notes,
        })
        .eq("id", po.id);

      if (error) throw error;

      // Estado real de las OS: aplicar al conjunto de service_orders de la OC.
      const { error: osStatusErr } = await supabase
        .from("service_orders")
        .update({ status: form.ops_status })
        .eq("po_id", po.id);

      if (osStatusErr) throw osStatusErr;

      setSaving(false);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      console.error("[OperationsModal] save error:", e);
      alert(e.message || "No se pudo guardar la gestión operativa");
      setSaving(false);
    }
  };

  const titleCode = (po.code || `OS-${po.base_number ?? ""}`).replace(/^OC-/i, "OS-");

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-lg border border-slate-700 bg-slate-900 p-4">
        <h3 className="text-lg font-semibold mb-1">Gestión operativa — {titleCode}</h3>
        <p className="text-xs text-slate-400 mb-4">
          Definí prioridad, estado, vencimiento y notas internas para esta orden.
          El técnico verá esta información en su panel.
        </p>

        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-300 mb-1">Prioridad</label>
              <select
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={form.ops_priority}
                onChange={(e) => handleChange("ops_priority", e.target.value)}
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-300 mb-1">
                Estado operativo
              </label>
              <select
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={form.ops_status}
                onChange={(e) => handleChange("ops_status", e.target.value)}
              >
                {SERVICE_ORDER_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">
              Fecha de vencimiento
            </label>
            <input
              type="date"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={form.ops_due_date || ""}
              onChange={(e) => handleChange("ops_due_date", e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">
              Notas para el técnico
            </label>
            <textarea
              rows={3}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none"
              value={form.ops_notes || ""}
              onChange={(e) => handleChange("ops_notes", e.target.value)}
              placeholder="Ej: ir primero a Baker, coordinar turno con planta, etc."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            className="rounded border border-slate-600/60 px-3 py-1 text-slate-300 hover:bg-slate-600/10"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="rounded border border-emerald-600/60 px-3 py-1 text-emerald-300 hover:bg-emerald-600/10 disabled:opacity-60"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
